import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Brain, Sparkles, Loader2, Check, Plus, Minus, Lightbulb, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_LABEL = {
  titles: 'Titles', industries: 'Industries', locations: 'Locations',
  seniorities: 'Seniorities', company_sizes: 'Company sizes', keyword: 'Keyword',
};

// Phase B — Brain "ICP Coach". Suggests concrete broadenings/refinements the rep
// can APPLY with one tap (human-in-the-loop). Applying routes through the same
// /refine-search source-of-truth so profile + search params stay in sync.
export const ICPCoachCard = ({ icp, onApplied }) => {
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get(`${API}/api/brain/icp/coach`);
      setCoach(r.data?.coach || null);
    } catch { setCoach(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post(`${API}/api/brain/icp/coach`);
      const c = r.data?.coach || null;
      setCoach(c);
      if (c?.error === 'credit_block') toast.error('Not enough credits to run the ICP Coach');
      else if (!c?.suggestions?.length) toast.info('Coach had no new suggestions — your targeting looks solid');
      else toast.success('ICP Coach analysed your targeting');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not run the ICP Coach');
    } finally { setRunning(false); }
  };

  const currentFor = (field) => {
    const sp = (icp && icp.apollo_search_params) || {};
    switch (field) {
      case 'titles': return icp?.titles || sp.titles || [];
      case 'industries': return icp?.industries || sp.raw_industries || [];
      case 'locations': return icp?.locations || sp.locations || [];
      case 'seniorities': return icp?.seniorities || sp.seniorities || [];
      case 'company_sizes': return icp?.company_sizes || (icp?.profile?.firmographic?.company_sizes) || [];
      default: return [];
    }
  };

  const applySuggestion = async (s) => {
    const action = s.action || {};
    const field = action.field;
    if (!field) return;
    setApplying((p) => ({ ...p, [s.id]: true }));
    try {
      let body = {};
      if (field === 'keyword') {
        body = { keyword: action.set != null ? action.set : '' };
      } else {
        const cur = currentFor(field);
        const removeSet = new Set((action.remove || []).map((x) => String(x).toLowerCase()));
        let merged = cur.filter((x) => !removeSet.has(String(x).toLowerCase()));
        for (const add of (action.add || [])) {
          if (!merged.some((x) => String(x).toLowerCase() === String(add).toLowerCase())) merged.push(add);
        }
        body = { [field]: merged };
      }
      await api.post(`${API}/api/brain/icp/refine-search`, body);
      toast.success(`Applied: ${s.title}`);
      // Remove the applied suggestion from the list.
      setCoach((c) => c ? { ...c, suggestions: (c.suggestions || []).filter((x) => x.id !== s.id) } : c);
      if (onApplied) await onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not apply suggestion');
    } finally {
      setApplying((p) => ({ ...p, [s.id]: false }));
    }
  };

  const suggestions = coach?.suggestions || [];
  const health = coach?.health;

  return (
    <div className="rounded-xl border border-violet-500/25 bg-violet-500/[0.04] p-5 shadow-sm"
         data-testid="icp-coach-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Brain className="w-4 h-4 text-violet-500" /> ICP Coach
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Let the Brain suggest smarter targeting — tap Apply to broaden your reach instantly.
          </p>
        </div>
        <button
          onClick={run}
          disabled={running}
          data-testid="icp-coach-run-btn"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 transition-colors self-start"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {suggestions.length || health ? 'Re-analyse' : 'Get suggestions'}
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground -mt-2 mb-3 flex items-center gap-1" data-testid="icp-coach-credit-hint">
        <span className="inline-block w-1 h-1 rounded-full bg-violet-500" /> Uses AI credits (billed per run)
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="icp-coach-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {health && (
            <div className="mb-4 flex items-start gap-2 text-sm rounded-lg bg-background/60 border border-border px-3 py-2"
                 data-testid="icp-coach-health">
              <Lightbulb className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
              <p className="text-foreground/80">
                <span className="font-medium capitalize">{health.assessment}</span>
                {health.note && <> — {health.note}</>}
              </p>
            </div>
          )}

          {suggestions.length === 0 ? (
            <div className="text-sm text-muted-foreground italic py-2" data-testid="icp-coach-empty">
              {coach?.coached_at
                ? 'No suggestions right now — your targeting looks well-balanced.'
                : 'Tap "Get suggestions" for a Brain review of your targeting.'}
            </div>
          ) : (
            <div className="space-y-2.5" data-testid="icp-coach-suggestions">
              {suggestions.map((s) => {
                const a = s.action || {};
                return (
                  <div key={s.id}
                    className="flex items-start justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2.5"
                    data-testid={`icp-coach-suggestion-${s.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{s.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{s.rationale}</p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        {a.field && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            {FIELD_LABEL[a.field] || a.field}
                          </span>
                        )}
                        {(a.add || []).map((x, i) => (
                          <span key={`add${i}`} className="inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600">
                            <Plus className="w-2.5 h-2.5" /> {x}
                          </span>
                        ))}
                        {(a.remove || []).map((x, i) => (
                          <span key={`rem${i}`} className="inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-600">
                            <Minus className="w-2.5 h-2.5" /> {x}
                          </span>
                        ))}
                        {a.field === 'keyword' && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600">
                            set keyword → "{a.set || '(clear)'}"
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => applySuggestion(s)}
                      disabled={applying[s.id]}
                      data-testid={`icp-coach-apply-${s.id}`}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 transition-colors"
                    >
                      {applying[s.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      Apply
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ICPCoachCard;
